import Badge from "@/components/common/Badge";
import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import PageHeader from "@/components/common/PageHeader";
import SearchBar from "@/components/common/SearchBar";
import { Dialog } from "@headlessui/react";
import {
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Policy type definition
interface Policy {
  id: string;
  title: string;
  version: string;
  effectiveDate: string;
  lastUpdated: string;
  status: string;
  acknowledged: boolean;
  acknowledgedDate?: string;
  description: string;
}

// Mock data - will be replaced with translation keys in component
const getMockPolicies = (t: any) => [
  {
    id: "1",
    title: t("policies.informationSecurityPolicy"),
    version: "2.1",
    effectiveDate: "2025-01-15",
    lastUpdated: "2024-12-05",
    status: "active",
    acknowledged: true,
    acknowledgedDate: "2025-01-20",
    description: t("policies.informationSecurityDescription"),
  },
  {
    id: "2",
    title: t("policies.acceptableUsePolicy"),
    version: "3.0",
    effectiveDate: "2025-02-01",
    lastUpdated: "2025-01-10",
    status: "active",
    acknowledged: false,
    description: t("policies.acceptableUseDescription"),
  },
  {
    id: "3",
    title: t("policies.dataClassificationPolicy"),
    version: "1.5",
    effectiveDate: "2024-11-01",
    lastUpdated: "2024-10-15",
    status: "active",
    acknowledged: true,
    acknowledgedDate: "2024-11-05",
    description: t("policies.dataClassificationDescription"),
  },
  {
    id: "4",
    title: t("policies.remoteWorkSecurityPolicy"),
    version: "2.0",
    effectiveDate: "2025-03-01",
    lastUpdated: "2025-02-15",
    status: "active",
    acknowledged: false,
    description: t("policies.remoteWorkSecurityDescription"),
  },
  {
    id: "5",
    title: t("policies.passwordPolicy"),
    version: "4.2",
    effectiveDate: "2024-12-01",
    lastUpdated: "2024-11-20",
    status: "active",
    acknowledged: true,
    acknowledgedDate: "2024-12-03",
    description: t("policies.passwordPolicyDescription"),
  },
];

// Mock policy content - will be replaced with translation keys
const getMockPolicyContent = (t: any) => `
# ${t("policies.informationSecurityPolicy")}

## 1. ${t("policies.purpose")}

${t("policies.informationSecurityPurpose")}

## 2. ${t("policies.scope")}

${t("policies.informationSecurityScope")}

## 3. ${t("policies.policyStatements")}

### 3.1 ${t("policies.informationClassification")}

${t("policies.informationClassificationDescription")}:
- ${t("policies.public")}: ${t("policies.publicDescription")}
- ${t("policies.internal")}: ${t("policies.internalDescription")}
- ${t("policies.confidential")}: ${t("policies.confidentialDescription")}
- ${t("policies.restricted")}: ${t("policies.restrictedDescription")}

### 3.2 ${t("policies.accessControl")}

- ${t("policies.accessControlPrinciple")}
- ${t("policies.uniqueIdentifiers")}
- ${t("policies.privilegedAccessControl")}
- ${t("policies.accessRevocation")}

### 3.3 ${t("policies.securityIncidents")}

- ${t("policies.incidentReporting")}
- ${t("policies.incidentInvestigation")}
- ${t("policies.incidentDocumentation")}

## 4. ${t("policies.compliance")}

${t("policies.complianceDescription")}

## 5. ${t("policies.review")}

${t("policies.reviewDescription")}
`;

const Policies = () => {
  const { t } = useTranslation();
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Get translated mock policies
  const mockPolicies = getMockPolicies(t);

  // Filter policies based on search term
  const filteredPolicies = useMemo(() => {
    if (!searchTerm.trim()) {
      return mockPolicies;
    }

    return mockPolicies.filter(
      (policy) =>
        policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        policy.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        policy.version.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, mockPolicies]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const openPolicyModal = (policy: Policy) => {
    setSelectedPolicy(policy);
    setIsPolicyModalOpen(true);
  };

  const handleAcknowledge = async () => {
    try {
      setAcknowledging(true);

      // Simulate API call to acknowledge policy
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // In a real app, this would be an API call
      // For now, we'll just close the modal as the mock data is static

      setAcknowledging(false);
      setIsPolicyModalOpen(false);
    } catch (error) {
      console.error("Failed to acknowledge policy:", error);
      setAcknowledging(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("policies.activePolicies")}
        description={t("policies.description")}
      />

      <div className="mb-4 sm:mb-6">
        <SearchBar
          placeholder={t("policies.searchPolicies")}
          onSearch={handleSearch}
          className="w-full sm:w-80"
        />
      </div>

      <Card>
        {/* Desktop Table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-3 lg:px-4 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                  {t("policies.policy")}
                </th>
                <th className="px-3 lg:px-4 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                  {t("policies.version")}
                </th>
                <th className="hidden px-3 lg:px-4 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white md:table-cell">
                  {t("policies.effectiveDate")}
                </th>
                <th className="hidden px-3 lg:px-4 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white lg:table-cell">
                  {t("policies.lastUpdated")}
                </th>
                <th className="px-3 lg:px-4 py-3.5 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                  {t("policies.status")}
                </th>
                <th className="px-3 lg:px-4 py-3.5 text-right text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPolicies.length > 0 ? (
                filteredPolicies.map((policy) => (
                  <motion.tr
                    key={policy.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="px-3 lg:px-4 py-4">
                      <div className="flex items-center">
                        <DocumentTextIcon className="mr-2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                            {policy.title}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                            {policy.description}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 lg:px-4 py-4 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                      {t("policies.versionPrefix")}{policy.version}
                    </td>
                    <td className="hidden whitespace-nowrap px-3 lg:px-4 py-4 text-xs sm:text-sm text-gray-600 dark:text-gray-300 md:table-cell">
                      {policy.effectiveDate}
                    </td>
                    <td className="hidden whitespace-nowrap px-3 lg:px-4 py-4 text-xs sm:text-sm text-gray-600 dark:text-gray-300 lg:table-cell">
                      {policy.lastUpdated}
                    </td>
                    <td className="whitespace-nowrap px-3 lg:px-4 py-4 text-xs sm:text-sm">
                      {policy.acknowledged ? (
                        <Badge variant="success" className="flex items-center text-xs">
                          <CheckCircleIcon className="mr-1 h-3 w-3" />
                          {t("policies.acknowledged")}
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="flex items-center text-xs">
                          <ClockIcon className="mr-1 h-3 w-3" />
                          {t("policies.acknowledgmentRequired")}
                        </Badge>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 lg:px-4 py-4 text-right text-xs sm:text-sm">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPolicyModal(policy)}
                        className="text-xs"
                      >
                        {t("policies.viewPolicy")}
                      </Button>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 lg:px-4 py-8 text-center text-xs sm:text-sm text-gray-500 dark:text-gray-400"
                  >
                    {t("policies.noPoliciesFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden space-y-3 p-4">
          {filteredPolicies.length > 0 ? (
            filteredPolicies.map((policy) => (
              <motion.div
                key={policy.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start min-w-0 flex-1">
                    <DocumentTextIcon className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white break-words">
                        {policy.title}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {policy.description}
                      </p>
                    </div>
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    {policy.acknowledged ? (
                      <Badge variant="success" className="flex items-center text-xs">
                        <CheckCircleIcon className="mr-1 h-3 w-3" />
                        {t("policies.acknowledged")}
                      </Badge>
                    ) : (
                      <Badge variant="warning" className="flex items-center text-xs">
                        <ClockIcon className="mr-1 h-3 w-3" />
                        {t("policies.acknowledgmentRequired")}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3 text-xs text-gray-500 dark:text-gray-400">
                  <div>
                    <span className="font-medium">{t("policies.version")}:</span> {policy.version}
                  </div>
                  <div>
                    <span className="font-medium">{t("policies.effectiveDate")}:</span> {policy.effectiveDate}
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openPolicyModal(policy)}
                  className="w-full text-xs"
                >
                  {t("policies.viewPolicy")}
                </Button>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("policies.noPoliciesFound")}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Policy Modal */}
      <Dialog
        open={isPolicyModalOpen}
        onClose={() => setIsPolicyModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
            <Dialog.Panel className="mx-auto max-h-[95vh] sm:max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
              <div className="flex items-center justify-between border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 dark:border-gray-700">
                <div className="min-w-0 flex-1 mr-4">
                  <Dialog.Title className="text-base sm:text-lg font-medium text-gray-900 dark:text-white break-words">
                    {selectedPolicy?.title}
                  </Dialog.Title>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {t("policies.version")} {selectedPolicy?.version}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPolicyModalOpen(false)}
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-300 flex-shrink-0"
                >
                  <span className="sr-only">{t("common.close")}</span>
                  <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="max-h-[65vh] sm:max-h-[60vh] overflow-y-auto px-4 sm:px-6 py-3 sm:py-4">
                <div className="prose prose-xs sm:prose-sm max-w-none dark:prose-invert">
                  {getMockPolicyContent(t).split("\n").map((line, i) => {
                    if (line.startsWith("# ")) {
                      return (
                        <h1 key={i} className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4 break-words">
                          {line.replace("# ", "")}
                        </h1>
                      );
                    }
                    if (line.startsWith("## ")) {
                      return (
                        <h2 key={i} className="text-base sm:text-lg lg:text-xl font-semibold mb-2 sm:mb-3 break-words">
                          {line.replace("## ", "")}
                        </h2>
                      );
                    }
                    if (line.startsWith("### ")) {
                      return (
                        <h3 key={i} className="text-sm sm:text-base lg:text-lg font-medium mb-2 sm:mb-3 break-words">
                          {line.replace("### ", "")}
                        </h3>
                      );
                    }
                    if (line.startsWith("- ")) {
                      return (
                        <li key={i} className="ml-3 sm:ml-4 mb-1 sm:mb-2 text-xs sm:text-sm break-words">
                          {line.replace("- ", "")}
                        </li>
                      );
                    }
                    return line ? (
                      <p key={i} className="text-xs sm:text-sm mb-2 sm:mb-3 break-words leading-relaxed">
                        {line}
                      </p>
                    ) : (
                      <br key={i} />
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 dark:border-gray-700 dark:bg-gray-800/80 gap-3 sm:gap-0">
                <div className="flex-1 min-w-0">
                  {selectedPolicy?.acknowledged ? (
                    <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                      <CheckCircleIcon className="mr-1 h-4 w-4 sm:h-5 sm:w-5 text-success-500 flex-shrink-0" />
                      <span className="break-words">
                        {t("policies.acknowledgedOn")}: {selectedPolicy?.acknowledgedDate}
                      </span>
                    </div>
                  ) : (
                    <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 break-words">
                      {t("policies.acknowledgmentDisclaimer")}
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={() => setIsPolicyModalOpen(false)}
                    className="w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {t("common.close")}
                  </Button>

                  {!selectedPolicy?.acknowledged && (
                    <Button
                      variant="primary"
                      loading={acknowledging}
                      onClick={handleAcknowledge}
                      className="w-full sm:w-auto text-xs sm:text-sm"
                    >
                      {t("policies.acknowledgePolicy")}
                    </Button>
                  )}
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Policies;
